import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { showToast } from '@/shared/utils/showToast';
import { useAuthStore } from '@/store/authStore';
import {
  createAddress,
  deleteAddress,
  fetchAddresses,
  fetchCities,
  fetchDistricts,
  fetchProvinces,
  fetchSubdistricts,
  setDefaultAddress,
  updateAddress,
} from '../apis/address';
import type { AddressBody } from '../types/address.types';

// The server caches the region lists for a day too
const REGION_STALE_TIME = 24 * 60 * 60 * 1000;

// Per user, so one account's addresses never show up for the next on the same browser
const useAddressesKey = () => ['addresses', useAuthStore((state) => state.user_id)];

/** Main address first. */
export const useAddresses = () =>
  useQuery({ queryKey: useAddressesKey(), queryFn: fetchAddresses });

export const useSaveAddress = () => {
  const queryClient = useQueryClient();
  const queryKey = useAddressesKey();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: AddressBody }) =>
      id ? updateAddress(id, body) : createAddress(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });
};

export const useDeleteAddress = () => {
  const queryClient = useQueryClient();
  const queryKey = useAddressesKey();
  return useMutation({
    mutationFn: deleteAddress,
    // Another address may have become the main one
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error) => {
      showToast({ type: 'error', title: 'Gagal menghapus alamat', message: error.message });
    },
  });
};

export const useSetDefaultAddress = () => {
  const queryClient = useQueryClient();
  const queryKey = useAddressesKey();
  return useMutation({
    mutationFn: setDefaultAddress,
    onSuccess: (list) => queryClient.setQueryData(queryKey, list),
    onError: (error) => {
      showToast({ type: 'error', title: 'Gagal mengubah alamat utama', message: error.message });
    },
  });
};

export const useProvinces = (enabled = true) =>
  useQuery({
    queryKey: ['regions', 'provinces'],
    queryFn: fetchProvinces,
    enabled,
    staleTime: REGION_STALE_TIME,
  });

export const useCities = (provinceId?: string) =>
  useQuery({
    queryKey: ['regions', 'cities', provinceId],
    queryFn: () => fetchCities(provinceId as string),
    enabled: !!provinceId,
    staleTime: REGION_STALE_TIME,
  });

export const useDistricts = (cityId?: string) =>
  useQuery({
    queryKey: ['regions', 'districts', cityId],
    queryFn: () => fetchDistricts(cityId as string),
    enabled: !!cityId,
    staleTime: REGION_STALE_TIME,
  });

export const useSubdistricts = (districtId?: string) =>
  useQuery({
    queryKey: ['regions', 'subdistricts', districtId],
    queryFn: () => fetchSubdistricts(districtId as string),
    enabled: !!districtId,
    staleTime: REGION_STALE_TIME,
  });

/** A 400/422 answer names the field it is about in `type`. */
export const fieldErrorOf = (error: unknown): { field: string; message: string } | null => {
  if (!isAxiosError<{ message?: string; type?: string }>(error)) return null;
  const data = error.response?.data;
  return data?.type && data.message ? { field: data.type, message: data.message } : null;
};
